import { useState, useEffect, useMemo } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  LayoutDashboard,
  Users,
  Target,
  TrendingUp,
  FileText,
  Upload,
  Settings,
  LogOut,
  Menu,
  X,
  Search,
  Bell,
  HelpCircle,
  BarChart3,
  ShieldCheck,
  Link2,
  Sparkles,
  Brush,
  Key,
  Book,
  Radio,
  Wallet,
  Filter,
  Globe,
  LayoutGrid,
  Network,
  ChevronDown,
  ChevronRight,
  PanelLeftClose,
  PanelLeft,
  Home,
  Briefcase,
  Activity,
  Database,
  Gauge,
  GitBranch,
  CheckCircle2,
  Mail,
  Phone,
  Building2,
  Plug,
  Video,
  Calendar,
  Presentation,
  ClipboardList,
  Ticket,
  UserCircle,
} from 'lucide-react'
import { authApi } from '../api/auth'
import AIPanel, { AIFloatingButton } from '../components/AIPanel'
import TenantSwitcher from '../components/TenantSwitcher'
import { useTenant } from '../contexts/TenantContext'

const getNavigationGroups = (isMasterContext) => [
  {
    id: 'home',
    name: 'Home',
    icon: Home,
    items: [
      { name: 'Dashboard', href: '/', icon: LayoutDashboard },
      { name: 'Executive View', href: '/executive', icon: Gauge },
      { name: 'Custom Dashboards', href: '/dashboard-builder', icon: LayoutGrid },
    ]
  },
  {
    id: 'people',
    name: 'People',
    icon: Users,
    items: [
      { name: 'Contacts', href: '/contacts', icon: Users },
      { name: 'Companies', href: '/companies', icon: Building2 },
      { name: 'Segments', href: '/segments', icon: Target },
    ]
  },
  {
    id: 'sales',
    name: 'Sales',
    icon: Briefcase,
    items: [
      { name: 'Deals', href: '/deals', icon: TrendingUp },
      { name: 'Conference Tickets', href: '/conference-tickets', icon: Ticket },
      { name: 'Landing Pages', href: '/landing-pages', icon: FileText },
      { name: 'Forms', href: '/forms', icon: ClipboardList },
      { name: 'Sponsorship Decks', href: '/sponsorship-decks', icon: Presentation },
      { name: 'Funnels', href: '/funnels', icon: Filter },
    ]
  },
  {
    id: 'conversations',
    name: 'Conversations',
    icon: Phone,
    items: [
      { name: 'Call Insights', href: '/call-insights', icon: Phone },
      { name: 'Meeting Bots', href: '/meeting-bots', icon: Video },
      { name: 'Bot Scheduler', href: '/calendar-sync', icon: Calendar },
    ]
  },
  {
    id: 'analytics',
    name: 'Analytics',
    icon: Activity,
    items: [
      { name: 'Overview', href: '/analytics', icon: BarChart3 },
      { name: 'Deck Analytics', href: '/sponsorship-analytics', icon: Presentation },
      { name: 'Data Quality', href: '/data-quality', icon: CheckCircle2 },
      { name: 'Data Lineage', href: '/data-lineage', icon: GitBranch },
      { name: 'Brand Citations', href: '/brand-citations', icon: Globe },
      { name: 'Metrics (NL)', href: '/metrics', icon: Sparkles },
    ]
  },
  {
    id: 'data',
    name: 'Data Management',
    icon: Database,
    items: [
      { name: 'Data Import', href: '/import', icon: Upload },
      { name: 'Data Sync', href: '/data-sync', icon: Link2 },
      { name: 'Data Cleaning', href: '/data-cleaning', icon: Brush },
      { name: 'Data Enrichment', href: '/data-enrichment', icon: Sparkles },
      { name: 'Email Verification', href: '/email-verification', icon: Mail },
      { name: 'Data Map', href: '/data-map', icon: Network },
    ]
  },
  {
    id: 'tracking',
    name: 'Tracking',
    icon: Radio,
    items: [
      { name: 'Revenue Attribution', href: '/revenue', icon: Wallet },
      { name: 'Pixel Management', href: '/pixel-management', icon: Radio },
      { name: 'Tracking Sites', href: '/tracking-sites', icon: Globe },
    ]
  },
  {
    id: 'settings',
    name: 'Settings',
    icon: Settings,
    items: [
      { name: 'API Management', href: '/api-management', icon: Key },
      { name: 'Users', href: '/users', icon: ShieldCheck },
      ...(isMasterContext ? [{ name: 'Tenants', href: '/tenants', icon: Building2 }] : []),
      { name: 'CommsOperator', href: '/kb-integration', icon: Plug },
      { name: 'Admin Settings', href: '/admin', icon: Settings },
      { name: 'Documentation', href: '/documentation', icon: Book },
    ]
  },
]

function NavGroup({ group, isExpanded, onToggle, currentPath, isCollapsed, searchQuery }) {
  const hasActiveItem = group.items.some(item => currentPath === item.href)
  const filteredItems = searchQuery 
    ? group.items.filter(item => item.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : group.items

  if (searchQuery && filteredItems.length === 0) return null

  if (isCollapsed) {
    return (
      <div className="relative group">
        <div
          className={`flex items-center justify-center p-2.5 rounded-lg cursor-pointer transition-colors ${
            hasActiveItem ? 'bg-primary-50 text-primary-600' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
          }`}
          title={group.name}
        >
          <group.icon className="h-5 w-5" />
        </div>
        <div className="absolute left-full top-0 ml-2 hidden group-hover:block z-50">
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 py-2 min-w-[180px]">
            <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              {group.name}
            </div>
            {group.items.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                  currentPath === item.href
                    ? 'bg-primary-50 text-primary-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <item.icon className="h-4 w-4" />
                {item.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="mb-1">
      <button
        onClick={onToggle}
        className={`flex items-center justify-between w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
          hasActiveItem && !isExpanded ? 'bg-primary-50 text-primary-600' : 'text-gray-600 hover:bg-gray-50'
        }`}
      >
        <div className="flex items-center gap-3">
          <group.icon className="h-4 w-4" />
          <span>{group.name}</span>
        </div>
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-gray-400" />
        ) : (
          <ChevronRight className="h-4 w-4 text-gray-400" />
        )}
      </button>
      
      {isExpanded && (
        <ul className="mt-1 ml-3 pl-3 border-l border-gray-200 space-y-0.5">
          {filteredItems.map((item) => {
            const isActive = currentPath === item.href
            return (
              <li key={item.href}>
                <Link
                  to={item.href}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActive
                      ? 'bg-primary-50 text-primary-600 font-medium'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <item.icon className="h-4 w-4" />
                  {item.name}
                </Link>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export default function MainLayout({ children, user }) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [aiPanelOpen, setAiPanelOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed')
    return saved === 'true'
  })
  const [expandedGroups, setExpandedGroups] = useState(() => {
    const saved = localStorage.getItem('expandedNavGroups')
    return saved ? JSON.parse(saved) : ['home']
  })
  const [navSearch, setNavSearch] = useState('')
  
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { isMasterContext } = useTenant()
  
  const navigationGroups = useMemo(() => getNavigationGroups(isMasterContext), [isMasterContext])

  useEffect(() => {
    const activeGroup = navigationGroups.find(group => 
      group.items.some(item => location.pathname === item.href)
    )
    if (activeGroup && !expandedGroups.includes(activeGroup.id)) {
      setExpandedGroups(prev => [...prev, activeGroup.id])
    }
  }, [location.pathname, navigationGroups])

  useEffect(() => {
    localStorage.setItem('expandedNavGroups', JSON.stringify(expandedGroups))
  }, [expandedGroups])

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', sidebarCollapsed.toString())
  }, [sidebarCollapsed])

  useEffect(() => {
    const handleOpenAIPanel = () => setAiPanelOpen(true)
    window.addEventListener('openAIPanel', handleOpenAIPanel)
    return () => window.removeEventListener('openAIPanel', handleOpenAIPanel)
  }, [])

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    )
  }

  const handleLogout = async () => {
    await authApi.logout()
    queryClient.clear()
    navigate('/login')
  }

  const filteredGroups = useMemo(() => {
    if (!navSearch) return navigationGroups
    return navigationGroups.filter(group => 
      group.name.toLowerCase().includes(navSearch.toLowerCase()) ||
      group.items.some(item => item.name.toLowerCase().includes(navSearch.toLowerCase()))
    )
  }, [navSearch, navigationGroups])

  return (
    <div className="min-h-screen bg-gray-50">
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        </div>
      )}

      <aside className={`fixed inset-y-0 left-0 z-50 bg-white shadow-lg transform transition-all duration-200 lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } ${sidebarCollapsed ? 'w-16 overflow-visible' : 'w-64'}`}>
        <div className={`flex flex-col h-full ${sidebarCollapsed ? 'overflow-visible' : ''}`}>
          <div className="flex items-center justify-between px-3 py-4 border-b border-gray-100">
            {!sidebarCollapsed ? (
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <span className="text-lg font-bold text-primary-500">Qoralytics</span>
              </div>
            ) : (
              <div className="w-full flex justify-center">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
              </div>
            )}
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden p-1.5 text-gray-500 hover:text-gray-700">
              <X className="h-5 w-5" />
            </button>
          </div>

          {!sidebarCollapsed && (
            <div className="px-3 py-3 border-b border-gray-100">
              <TenantSwitcher />
            </div>
          )}

          {!sidebarCollapsed && (
            <div className="px-3 py-3 border-b border-gray-100">
              <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                <Search className="h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search menu..."
                  value={navSearch}
                  onChange={(e) => setNavSearch(e.target.value)}
                  className="flex-1 bg-transparent text-sm outline-none placeholder-gray-400"
                />
                {navSearch && (
                  <button onClick={() => setNavSearch('')} className="text-gray-400 hover:text-gray-600">
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          )}

          {!sidebarCollapsed && (
            <div className="px-3 py-3 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full gradient-bg flex items-center justify-center text-white font-semibold text-sm">
                  {user?.name?.[0]?.toUpperCase() || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
                  <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                </div>
              </div>
            </div>
          )}

          <nav className={`flex-1 px-2 py-3 ${sidebarCollapsed ? 'overflow-visible' : 'overflow-y-auto'}`}>
            {sidebarCollapsed ? (
              <div className="space-y-1">
                {navigationGroups.map((group) => (
                  <NavGroup
                    key={group.id}
                    group={group}
                    isExpanded={false}
                    onToggle={() => {}}
                    currentPath={location.pathname}
                    isCollapsed={true}
                    searchQuery=""
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-0.5">
                {filteredGroups.map((group) => (
                  <NavGroup
                    key={group.id}
                    group={group}
                    isExpanded={expandedGroups.includes(group.id) || !!navSearch}
                    onToggle={() => toggleGroup(group.id)}
                    currentPath={location.pathname}
                    isCollapsed={false}
                    searchQuery={navSearch}
                  />
                ))}
              </div>
            )}
          </nav>

          <div className="px-2 py-3 border-t border-gray-100 space-y-1">
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="hidden lg:flex items-center justify-center gap-2 w-full px-3 py-2 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors"
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {sidebarCollapsed ? (
                <PanelLeft className="h-5 w-5" />
              ) : (
                <>
                  <PanelLeftClose className="h-5 w-5" />
                  <span>Collapse</span>
                </>
              )}
            </button>
            <Link
              to="/account"
              className={`flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors ${
                sidebarCollapsed ? 'justify-center' : ''
              }`}
              title="My Account"
            >
              <UserCircle className="h-5 w-5" />
              {!sidebarCollapsed && <span>My Account</span>}
            </Link>
            <button
              onClick={handleLogout}
              className={`flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors ${
                sidebarCollapsed ? 'justify-center' : ''
              }`}
              title="Sign Out"
            >
              <LogOut className="h-5 w-5" />
              {!sidebarCollapsed && <span>Sign Out</span>}
            </button>
          </div>
        </div>
      </aside>

      <div className={`transition-all duration-200 ${sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'}`}>
        <header className="sticky top-0 z-30 bg-white border-b border-gray-100">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 text-gray-500 hover:text-gray-700"
              >
                <Menu className="h-5 w-5" />
              </button>
              
              <div className="hidden md:flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 w-80">
                <Search className="h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search contacts, pages..."
                  className="flex-1 bg-transparent text-sm outline-none"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-50">
                <Bell className="h-5 w-5" />
              </button>
              <button className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-50">
                <HelpCircle className="h-5 w-5" />
              </button>
            </div>
          </div>
        </header>

        <main className="p-6">
          {children}
        </main>
      </div>

      <AIFloatingButton onClick={() => setAiPanelOpen(true)} />
      <AIPanel isOpen={aiPanelOpen} onClose={() => setAiPanelOpen(false)} />
    </div>
  )
}
